'use client'

import { useQuery } from '@tanstack/react-query'
import { play } from 'cuelume'
import { useAtomValue, useStore } from 'jotai'
import { useEffect } from 'react'

import { showResearchNotificationToast } from '@/components/ui/notification-toast-helpers'
import { useAuth } from '@/features/auth/hooks'
import { toWidgetExplainReportData } from '@/features/order/chat/components/responses/widget-explain-report-data'
import {
  buildCompletedPhaseSteps,
  buildDeliveredPhaseSteps,
} from '@/features/order/chat/lib/research-status-timeline'
import {
  readStoredResearchJobs,
  shouldStoreResearchJob,
  type StoredResearchJob,
  writeStoredResearchJobs,
} from '@/features/order/research/research-active-jobs'
import { useOpenResearchThread } from '@/features/order/research/use-open-research-thread'
import { INITIAL_RESEARCH_STATUS_TEXT } from '@/features/order/research/use-research-manager'
import { getCurrentUserId } from '@/lib/safe-storage'
import type { ChatMessage } from '@/lib/types'
import {
  getResearchReport,
  ResearchApiError,
  type ResearchPollResult,
} from '@/service/research.api'
import {
  activeThreadIdsAtom,
  ASSISTANT_CARD_ID,
  ASSISTANT_SURFACE_ID,
  clearChatThreadsAtom,
  messagesAtomFamily,
  registerChatThreadAtom,
  researchConversationAtomFamily,
  threadMetaAtomFamily,
  widgetExplainPendingAtomFamily,
} from '@/state/chat'
import {
  activeResearchJobsAtom,
  type ResearchJob,
  researchJobsAtom,
  researchNotificationsAtom,
} from '@/state/research'

const POLL_INTERVAL_MS = 10_000
const QUERY_GC_TIME_MS = 30 * 60 * 1000

function canRetry(error: unknown) {
  return (
    !(error instanceof ResearchApiError) || ['unknown', 'upstream', 'server'].includes(error.kind)
  )
}

function latestProgressLabel(result: ResearchPollResult) {
  return [...result.events].reverse().find((event) => event.label)?.label
}

function ResearchJobPoller({ job }: { job: ResearchJob }) {
  const store = useStore()
  const query = useQuery({
    queryKey: ['research', 'job', job.jobId],
    queryFn: () => getResearchReport(job.jobId as string),
    enabled: !!job.jobId,
    staleTime: POLL_INTERVAL_MS,
    gcTime: QUERY_GC_TIME_MS,
    retry: (failureCount, error) => canRetry(error) && failureCount < 5,
    retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 30_000),
    refetchInterval: (queryState) => {
      const phase = queryState.state.data?.phase
      return phase === 'DELIVERED' ||
        phase === 'FAILED' ||
        phase === 'KILLED' ||
        phase === 'CANCELLED'
        ? false
        : POLL_INTERVAL_MS
    },
    // Active jobs already have a 10-second interval. Focus should not add a
    // second, timing-dependent request on top of that polling cadence.
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: true,
    networkMode: 'online',
  })

  useEffect(() => {
    const result = query.data
    if (!result) return
    const now = Date.now()
    store.set(researchJobsAtom, (jobs) => {
      const current = jobs[job.id]
      if (!current) return jobs
      const isTerminal =
        result.phase === 'DELIVERED' ||
        result.phase === 'FAILED' ||
        result.phase === 'KILLED' ||
        result.phase === 'CANCELLED'
      const completedAt = isTerminal ? (current.completedAt ?? now) : null
      return {
        ...jobs,
        [job.id]: {
          ...current,
          phase: result.phase,
          events: result.events,
          report: result.phase === 'DELIVERED' ? result.report : current.report,
          error:
            result.phase === 'FAILED' || result.phase === 'KILLED' || result.phase === 'CANCELLED'
              ? (result.error ??
                (result.phase === 'CANCELLED'
                  ? 'Research cancelled.'
                  : 'Research failed. Please try again.'))
              : null,
          updatedAt: now,
          completedAt,
          cancelRequestedAt: isTerminal ? null : current.cancelRequestedAt,
          // A restored job found still running should notify when it finishes, like a live one.
          notifiedAt: current.restored && !isTerminal ? null : current.notifiedAt,
          restored: false,
        },
      }
    })

    const label = latestProgressLabel(result) ?? INITIAL_RESEARCH_STATUS_TEXT
    const history = buildCompletedPhaseSteps(result.events)
    store.set(messagesAtomFamily(job.threadId), (messages) =>
      messages.map((message) => {
        if (message.id !== job.assistantMessageId) return message
        if (result.phase === 'DELIVERED') {
          // The status line goes away with the report; its flow stays as a collapsed trace.
          const steps = buildDeliveredPhaseSteps(result.events, result.report.meta?.durationSeconds)
          return {
            ...message,
            parts: [
              ...(steps.length > 0
                ? [
                    {
                      type: 'component' as const,
                      componentType: 'RESEARCH_TRACE' as const,
                      data: { steps },
                    },
                  ]
                : []),
              {
                type: 'component',
                componentType: 'WIDGET_EXPLAIN_REPORT',
                data: toWidgetExplainReportData(result.report, {
                  showFollowupActions: job.kind !== 'widget-explain',
                }),
              },
            ],
          }
        }
        if (
          result.phase === 'FAILED' ||
          result.phase === 'KILLED' ||
          result.phase === 'CANCELLED'
        ) {
          return {
            ...message,
            parts: [
              {
                type: 'text',
                text:
                  result.error ??
                  (result.phase === 'CANCELLED'
                    ? 'Research cancelled.'
                    : 'Research failed. Please try again.'),
              },
            ],
          }
        }
        return {
          ...message,
          parts: [
            {
              type: 'component',
              componentType: 'RESEARCH_STATUS',
              data: { text: label, history },
            },
          ],
        }
      })
    )
    if (
      result.phase === 'DELIVERED' ||
      result.phase === 'FAILED' ||
      result.phase === 'KILLED' ||
      result.phase === 'CANCELLED'
    ) {
      store.set(widgetExplainPendingAtomFamily(job.threadId), false)
    }
  }, [job.assistantMessageId, job.id, job.kind, job.threadId, query.data, store])

  useEffect(() => {
    if (!(query.error instanceof ResearchApiError) || query.error.kind !== 'not_found') return
    const now = Date.now()
    const message = query.error.message
    store.set(researchJobsAtom, (jobs) => ({
      ...jobs,
      [job.id]: {
        ...jobs[job.id],
        phase: 'FAILED',
        error: message,
        updatedAt: now,
        completedAt: now,
      },
    }))
    store.set(messagesAtomFamily(job.threadId), (messages) =>
      messages.map((entry) =>
        entry.id === job.assistantMessageId
          ? { ...entry, parts: [{ type: 'text', text: message }] }
          : entry
      )
    )
    store.set(widgetExplainPendingAtomFamily(job.threadId), false)
  }, [job.assistantMessageId, job.id, job.threadId, query.error, store])

  return null
}

function ResearchTerminalNotifier({ job }: { job: ResearchJob }) {
  const store = useStore()
  const openThread = useOpenResearchThread()

  useEffect(() => {
    if (job.notifiedAt) return
    const timestamp = job.completedAt ?? Date.now()
    const success = job.phase === 'DELIVERED'
    const entry = {
      id: `research:${job.id}`,
      source: 'research' as const,
      category: 'research' as const,
      severity: success ? ('success' as const) : ('error' as const),
      title: success ? 'Research complete' : 'Research failed',
      message: success ? job.question : (job.error ?? job.question),
      timestamp,
      jobId: job.jobId ?? job.id,
      threadId: job.threadId,
      readAt: null,
      dismissedAt: null,
    }
    store.set(researchNotificationsAtom, (current) =>
      current.some((notification) => notification.id === entry.id) ? current : [entry, ...current]
    )
    store.set(researchJobsAtom, (current) => ({
      ...current,
      [job.id]: { ...current[job.id], notifiedAt: timestamp },
    }))
    play(success ? 'ready' : 'error')
    showResearchNotificationToast(entry, () => openThread(job.threadId))
  }, [job, openThread, store])

  return null
}

type Store = ReturnType<typeof useStore>

function toStoredResearchJob(store: Store, job: ResearchJob): StoredResearchJob {
  const meta = store.get(threadMetaAtomFamily(job.threadId))
  return {
    id: job.id,
    jobId: job.jobId as string,
    threadId: job.threadId,
    serverThreadId: store.get(researchConversationAtomFamily(job.threadId)).serverThreadId,
    thread: meta
      ? {
          surfaceId: meta.surfaceId,
          cardId: meta.cardId,
          pageId: meta.pageId,
          source: meta.source,
          title: meta.title,
        }
      : undefined,
    kind: job.kind,
    parentJobId: job.parentJobId,
    rootJobId: job.rootJobId,
    question: job.question,
    assistantMessageId: job.assistantMessageId,
    startedAt: job.startedAt,
  }
}

/**
 * Puts stored runs back: each gets its thread, the question and a status line to report into,
 * and a PENDING job the poller picks up — the dock lists it again, and the first poll tells
 * whether it is still running or landed while the page was closed (then it notifies).
 */
function restoreStoredResearchJobs(store: Store, entries: StoredResearchJob[]) {
  const now = Date.now()
  const restored: Record<string, ResearchJob> = {}
  for (const entry of [...entries].sort((a, b) => a.startedAt - b.startedAt)) {
    store.set(registerChatThreadAtom, {
      threadId: entry.threadId,
      surfaceId: entry.thread?.surfaceId ?? ASSISTANT_SURFACE_ID,
      cardId: entry.thread?.cardId ?? ASSISTANT_CARD_ID,
      pageId: entry.thread?.pageId,
      source: entry.thread?.source ?? 'panel',
      title: entry.thread?.title || entry.question,
      createdAt: entry.startedAt,
      updatedAt: entry.startedAt,
    })
    if (entry.serverThreadId) {
      store.set(researchConversationAtomFamily(entry.threadId), (prev) => ({
        ...prev,
        serverThreadId: entry.serverThreadId,
        historyLoaded: false,
      }))
    }
    const seeded: ChatMessage[] = [
      {
        id: `research-user-${entry.id}`,
        role: 'user',
        parts: [{ type: 'text', text: entry.question }],
      },
      {
        id: entry.assistantMessageId,
        role: 'assistant',
        parts: [
          {
            type: 'component',
            componentType: 'RESEARCH_STATUS',
            data: { text: INITIAL_RESEARCH_STATUS_TEXT },
          },
        ],
      },
    ]
    store.set(messagesAtomFamily(entry.threadId), (messages) => [...messages, ...seeded])
    restored[entry.id] = {
      id: entry.id,
      jobId: entry.jobId,
      clientRequestId: entry.id,
      threadId: entry.threadId,
      kind: entry.kind,
      parentJobId: entry.parentJobId,
      rootJobId: entry.rootJobId,
      question: entry.question,
      assistantMessageId: entry.assistantMessageId,
      phase: 'PENDING',
      events: [],
      report: null,
      error: null,
      startedAt: entry.startedAt,
      updatedAt: now,
      completedAt: null,
      notifiedAt: null,
      viewedAt: null,
    }
  }
  store.set(researchJobsAtom, restored)
}

export function ResearchSupervisor() {
  const { user } = useAuth()
  const store = useStore()
  const activeJobs = useAtomValue(activeResearchJobsAtom)
  const jobs = useAtomValue(researchJobsAtom)
  const activeThreadIds = useAtomValue(activeThreadIdsAtom)
  const userId = user?.userId ?? getCurrentUserId()

  // Conversations live on the server (GET /research/threads, loaded when a thread is opened).
  // Locally only the in-flight runs are kept, per user, so the dock and notifications survive a
  // reload. Synced through a store subscription rather than an effect on `jobs`: an effect in
  // this same commit would still see the pre-restore empty map and wipe the stored runs.
  useEffect(() => {
    store.set(clearChatThreadsAtom)
    store.set(researchJobsAtom, {})
    store.set(researchNotificationsAtom, [])
    if (userId === 'guest' || userId === 'ssr') return

    restoreStoredResearchJobs(store, readStoredResearchJobs(userId))
    return store.sub(researchJobsAtom, () => {
      writeStoredResearchJobs(
        userId,
        Object.values(store.get(researchJobsAtom))
          .filter(shouldStoreResearchJob)
          .map((job) => toStoredResearchJob(store, job))
      )
    })
  }, [store, userId])

  const visibleThreadIds = new Set(Object.values(activeThreadIds))
  const jobsToPoll = Object.values(jobs).filter(
    (job) =>
      !!job.jobId &&
      (activeJobs.some((active) => active.id === job.id) ||
        (job.phase === 'DELIVERED' && visibleThreadIds.has(job.threadId) && job.report === null))
  )
  const jobsToNotify = Object.values(jobs).filter(
    (job) =>
      !job.notifiedAt &&
      (job.phase === 'DELIVERED' || job.phase === 'FAILED' || job.phase === 'KILLED')
  )

  return (
    <>
      {jobsToPoll.map((job) => (
        <ResearchJobPoller key={job.id} job={job} />
      ))}
      {jobsToNotify.map((job) => (
        <ResearchTerminalNotifier key={job.id} job={job} />
      ))}
    </>
  )
}
